import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import DataTable from 'src/components/ui/data-table';
import { StatusBadge } from 'src/components/common/StatusBadge';
import {
  Info,
  FileText,
  Building,
  AlertCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/hooks/use-toast';
import BillingTabs from 'src/pages/Billing/BillingTabs/BillingTabs';
import StatsSection from 'src/components/common/StatsSection';
import { useCreditNoteList } from 'src/services/api/queries/billingQueries';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';

const DEFAULT_PAGE_SIZE = 10;

const AmountCell = ({ value, currency }) => (
  <div className="flex items-center">
    {currency} {value}
  </div>
);

const OrganizationCell = ({ value, row }) => {
  const customerCategory = row?.customer_category;

  if (!customerCategory) {
    return (
      <div className="flex items-center">
        <Building className="w-4 h-4 mr-2" />
        {value}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className="flex items-center cursor-pointer">
            <Building className="w-4 h-4 mr-2" />
            {value}
          </div>
        </TooltipTrigger>
        <TooltipContent>{customerCategory}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const getColumns = () => [
  {
    key: 'credit_note_number',
    header: 'Credit Note Number',
  },
  {
    key: 'requirement_title',
    header: 'Requirement',
  },
  {
    key: 'organization_name',
    header: 'Customer',
    render: (value, row) => <OrganizationCell value={value} row={row} />,
  },
  {
    key: 'po_number',
    header: 'PO Number',
  },
  {
    key: 'candidate_name',
    header: 'Candidate Name',
  },
  {
    key: 'amount',
    header: (
      <div className="flex items-center gap-1">
        Total Amount
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="p-0 m-0 bg-transparent border-0 cursor-pointer"
                aria-label="Info: GST exclusive"
                tabIndex={0}
              >
                <Info className="w-4 h-4 text-white-500" />
              </button>
            </TooltipTrigger>
            <TooltipContent>GST exclusive</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    render: (value, row) => (
      <AmountCell value={value} currency={row.currency} />
    ),
  },
  {
    key: 'reason',
    header: 'Reason',
    render: (value, row) => {
      const reason = value;
      const details = row?.details;
      if (!reason) return '';
      return (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="inline-block align-middle cursor-pointer flex items-center gap-2">
                {reason}
                <Info className="w-4 h-4 text-primary" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{details || reason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
  {
    key: 'status',
    header: 'Status',
    render: (value) => <StatusBadge value={value} />,
  },
  {
    key: 'issued_date',
    header: 'Issued',
    render: (value) => {
      const val = value;
      if (!val) return '';
      const date = new Date(val);
      if (Number.isNaN(date)) return val; // fallback if invalid date
      const day = String(date.getDate()).padStart(2, '0');
      const month = date
        .toLocaleString('en-US', { month: 'short' })
        .toUpperCase();
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
  },
];

function ListCreditNotes() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract params from URL
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  // You may want to get these from filters/UI in the future
  const status = searchParams.get('status') || 'DRAFT';
  // const organizationId = searchParams.get('organizationId') || '2';

  // Fetch credit notes using react-query
  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useCreditNoteList({ page, pageSize, status });

  // Extract items and pagination from API response
  const items = response?.data?.items || [];
  const pagination = response?.data?.pagination || {
    page: 1,
    limit: pageSize,
    total: 0,
    pages: 1,
  };

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    if (params.status) updatedParams.set('status', params.status);
    if (params.organizationId)
      updatedParams.set('organizationId', params.organizationId);
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    const tempPage = parseInt(newPage, 10);
    updateUrlParams({ page: tempPage });
  };

  const handlePageSizeChange = (newPageSize) => {
    const size = parseInt(newPageSize, 10);
    updateUrlParams({ pageSize: size, page: 1 });
  };

  React.useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (isError) {
      toast({
        title: 'Error',
        description:
          error?.message ||
          'Failed to load credit notes. Please refresh the page.',
        variant: 'destructive',
      });
    }
    // eslint-disable-next-line
  }, [isError, error]);

  const columns = getColumns();

  const draftCount = items.filter((i) => i.status === 'DRAFT').length;
  const issuedCount = items.filter(
    (i) => i.status === 'ISSUED' || i.status === 'APPROVED'
  ).length;

  return (
    <div>
      <PageHeader title="Credit Notes" />
      <PageContainer>
        <StatsSection
          title="Summary"
          className="mb-5"
          cards={[
            {
              key: 'total',
              label: 'Total Credit Notes',
              value: pagination.total || 0,
              iconColor: 'var(--primary)',
              icon: (
                <FileText style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'draft',
              label: 'Draft',
              value: draftCount,
              iconColor: '#B45309',
              icon: (
                <Clock style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'issued',
              label: 'Issued',
              value: issuedCount,
              iconColor: '#0D9668',
              icon: (
                <CheckCircle
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
          ]}
        />

        <BillingTabs />

        {isError ? (
          <div className="flex flex-col items-center justify-center py-16 px-5 text-center bg-card rounded-xl border border-border">
            <div
              className="flex items-center justify-center mb-5 rounded-full bg-ds-danger-subtle"
              style={{ width: 72, height: 72 }}
            >
              <AlertCircle
                className="text-ds-danger"
                style={{ width: 32, height: 32, strokeWidth: 1.5 }}
              />
            </div>
            <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
              Failed to load credit notes
            </p>
            <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs mb-4">
              {error?.message ||
                'An error occurred while fetching credit notes.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
            >
              Try again
            </Button>
          </div>
        ) : (
          <DataTable
            data={items}
            columns={columns}
            loading={isLoading}
            rowKey="id"
            hover
            stickyHeader
            onRowClick={(row) => navigate(`/credit-notes/${row?.id}`)}
            pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            pageSize={pagination.limit}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="border-border shadow-[0_1px_3px_rgba(0,0,0,.04)]"
            emptyState={
              <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
                <div
                  className="flex items-center justify-center mb-5 rounded-full bg-border"
                  style={{ width: 72, height: 72 }}
                >
                  <FileText
                    className="text-muted-foreground"
                    style={{ width: 32, height: 32, strokeWidth: 1.5 }}
                  />
                </div>
                <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
                  No credit notes found
                </p>
                <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs">
                  Credit notes will appear here once they are generated.
                </p>
              </div>
            }
          />
        )}
      </PageContainer>
    </div>
  );
}

export default ListCreditNotes;
